/**
 * What a valid final score is, and what one game is worth to each team on each board.
 *
 * The rules (5 Oct 2026): to 11, win by 2, no cap on points. At the time cap the leader
 * wins; level means one sudden-death point, so a game never ends level. An event can change
 * the target (first to 7) or switch win-by-2 off (the golden point).
 */

import type { Config, EventType, GameRecord, Pair, Score } from "./types";

export interface GameRules {
  target: number;
  winBy2: boolean;
}

export function eventById(config: Config, id: string | null): EventType | null {
  if (!id) return null;
  return config.eventTypes.find((e) => e.id === id) ?? null;
}

export function rulesFor(config: Config, event: EventType | null): GameRules {
  return {
    target: event?.target ?? config.target,
    winBy2: event?.winBy2 ?? config.winBy2,
  };
}

/** True when hi-lo is a game that finished by the rules alone (no clock involved). */
export function isFinished(hi: number, lo: number, rules: GameRules): boolean {
  if (hi < rules.target) return false;
  if (rules.winBy2) {
    return (hi === rules.target && lo <= rules.target - 2) || (hi > rules.target && hi - lo === 2);
  }
  return hi === rules.target && lo < rules.target;
}

/** Null when the score is valid, otherwise the reason in plain words for the tablet. */
export function scoreProblem(score: Score, rules: GameRules): string | null {
  const { a, b, capped } = score;
  if (!Number.isInteger(a) || !Number.isInteger(b) || a < 0 || b < 0) {
    return "Points must be whole numbers, 0 or more";
  }
  if (a === b) return "A game cannot end level: play one more point";
  const hi = Math.max(a, b);
  const lo = Math.min(a, b);
  if (isFinished(hi, lo, rules)) return null;
  if (capped) {
    // The clock ended it, so any lead wins. Only a score the rules would already have ended earlier
    // is impossible: 13-10 under win-by-2 (it was over at 12-10), or anything past the target without it.
    if (rules.winBy2 && hi > rules.target && hi - lo > 2) {
      return `${hi}-${lo} cannot happen: the game was over at ${Math.max(rules.target, lo + 2)}-${lo}`;
    }
    if (!rules.winBy2 && hi > rules.target) {
      return `First to ${rules.target}: the game was over at ${rules.target}`;
    }
    return null;
  }
  if (hi < rules.target) {
    return `The winner needs ${rules.target} points, or mark that the time ran out`;
  }
  // past the target with a lead of more than two: it was already over (a Quickfire 11-5, say)
  if (rules.winBy2 && hi > rules.target && hi - lo > 2) {
    return `First to ${rules.target}, win by 2: the game was over at ${Math.max(rules.target, lo + 2)}-${lo}`;
  }
  if (rules.winBy2) return `Win by 2: ${hi}-${lo} is not a finished game`;
  return `First to ${rules.target}: ${hi}-${lo} is not a finished game`;
}

export interface TeamResult {
  players: Pair;
  /** Raw points as entered. */
  raw: number;
  rawAgainst: number;
  won: boolean;
  /** Points on the main board (scaled to the normal target, event multiplier and bonus if they count). */
  mainPoints: number;
  mainAgainst: number;
  /** Points on the fun board (0 when no event was on). */
  funPoints: number;
}

export interface GameResult {
  a: TeamResult;
  b: TeamResult;
  event: EventType | null;
}

/** Null while the game is unscored or voided. */
export function gameResult(game: GameRecord, config: Config): GameResult | null {
  if (game.voided || !game.score) return null;
  const event = eventById(config, game.eventId);
  const scale = event?.target ? config.target / event.target : 1;
  const mainMult = event && event.countsOnMain ? event.mainMultiplier : 1;
  const mainBonus = event && event.countsOnMain ? event.winBonus : 0;
  const side = (players: Pair, mine: number, theirs: number): TeamResult => {
    const won = mine > theirs;
    return {
      players,
      raw: mine,
      rawAgainst: theirs,
      won,
      mainPoints: mine * scale * mainMult + (won ? mainBonus : 0),
      mainAgainst: theirs * scale * mainMult,
      funPoints: event ? mine * scale * event.funMultiplier + (won ? event.winBonus : 0) : 0,
    };
  };
  return {
    a: side(game.a, game.score.a, game.score.b),
    b: side(game.b, game.score.b, game.score.a),
    event,
  };
}
